import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { Project } from '@/api/client';

export const PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY = ['projects', 'table-active-ids'] as const;

export const ARCHIVED_PROJECT_IDS_QUERY_KEY = ['archived-project-ids'] as const;
export const ARCHIVED_COUNT_QUERY_KEY = ['projects', 'archived-count'] as const;

export function readyMonthProjectIdsQueryKey(year: number, month: number) {
  return ['ready-month-project-ids', year, month] as const;
}

export function projectEntityQueryKey(projectId: string) {
  return ['project', projectId] as const;
}

function reuseStringListIfUnchanged(
  prev: string[] | undefined,
  next: string[]
): string[] {
  if (!prev) return next;
  if (prev.length !== next.length) return next;
  for (let i = 0; i < prev.length; i += 1) {
    if (prev[i] !== next[i]) return next;
  }
  return prev;
}

export function setProjectEntity(queryClient: QueryClient, project: Project): void {
  const key = projectEntityQueryKey(project.id);
  const prev = queryClient.getQueryData<Project>(key);
  if (!prev || prev !== project) {
    queryClient.setQueryData<Project>(key, project);
  }
}

/** Ответ поиска: только entity-кэш, без патча списков активных/архива/месяцев. */
export function mergeProjectsIntoSearchEntityCache(queryClient: QueryClient, projects: Project[]): void {
  for (const project of projects) {
    setProjectEntity(queryClient, project);
  }
}

export function syncProjectEntities(queryClient: QueryClient, projects: Project[]): void {
  for (const project of projects) {
    setProjectEntity(queryClient, project);
  }
}

export function syncProjectIdsQuery(
  queryClient: QueryClient,
  key: QueryKey,
  projects: Project[]
): void {
  const nextIds = projects.map((project) => project.id);
  queryClient.setQueryData<string[]>(key, (prev) => reuseStringListIfUnchanged(prev, nextIds));
}

export function removeProjectEntity(queryClient: QueryClient, projectId: string): void {
  queryClient.removeQueries({ queryKey: projectEntityQueryKey(projectId) });
}

export function getProjectEntityFromCaches(
  queryClient: QueryClient,
  projectId: string,
  fallback?: Project
): Project | undefined {
  const entity = queryClient.getQueryData<Project>(projectEntityQueryKey(projectId));
  if (entity) return entity;

  return fallback;
}

export function subscribeProjectEntity(
  queryClient: QueryClient,
  projectId: string,
  onStoreChange: () => void
): () => void {
  return queryClient.getQueryCache().subscribe((event) => {
    const key = event?.query?.queryKey as unknown[] | undefined;
    if (!Array.isArray(key)) return;
    // Только сущность этой строки. Подписка на весь `table-active-ids` вызывала
    // onStoreChange у каждой строки при любом патче списка (лишние ререндеры).
    if (key[0] === 'project' && key[1] === projectId) {
      onStoreChange();
    }
  });
}
